import styled from "@emotion/styled/macro";
import {
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { OfferReviewComparisonGauge } from "components/molecules/offerReviewComparison/OfferReviewComparisonGauge";
import { OfferReviewComparisonHouseIllustration } from "components/molecules/offerReviewComparison/OfferReviewComparisonHouseIllustration";

type ComparisonLayout = "portrait" | "landscape";

type OfferReviewComparisonInfographicCardProps = {
  variant: "utility" | "solar";
  layout?: ComparisonLayout;
  sunrunPercent: number;
  gaugePercent: number;
  gaugeLabel: string;
  providerName?: string;
  recapBadge: string;
  recapBadgeVariant?: "outline" | "filled";
  headline: string;
  bodyLines: string[];
  featureList: string[];
  designLabel?: string;
};

const OfferReviewComparisonInfographicCard = ({
  variant,
  layout = "portrait",
  sunrunPercent,
  gaugePercent,
  gaugeLabel,
  providerName = "",
  recapBadge,
  recapBadgeVariant,
  headline,
  bodyLines,
  featureList,
  designLabel,
}: OfferReviewComparisonInfographicCardProps) => {
  const isUtility = variant === "utility";
  const badgeVariant = recapBadgeVariant ?? (isUtility ? "outline" : "filled");
  const gaugeVariant = isUtility ? "negative" : "positive";

  const header = (
    <CardHeader $landscape={layout === "landscape"}>
      {designLabel ? (
        <DesignLabel>{designLabel}</DesignLabel>
      ) : (
        <RecapBadge variant={badgeVariant}>{recapBadge}</RecapBadge>
      )}
      <RecapHeadline
        size={layout === "landscape" ? 24 : 24}
        fontWeight="bold"
        color={tokens.BLACK}
        align="center"
      >
        {headline}
      </RecapHeadline>
      <RecapBody>
        {bodyLines.map((line) => (
          <RecapBodyLine
            key={line}
            size={14}
            fontWeight="regular"
            color={tokens.BLACK}
            align="center"
          >
            {line}
          </RecapBodyLine>
        ))}
      </RecapBody>
    </CardHeader>
  );

  const illustration = (
    <OfferReviewComparisonHouseIllustration
      variant={variant}
      sunrunPercent={sunrunPercent}
    />
  );

  const listSection = (
    <ListSection $landscape={layout === "landscape"}>
      <GaugeSlot>
        <OfferReviewComparisonGauge
          percent={gaugePercent}
          variant={gaugeVariant}
          label={gaugeLabel}
          providerName={isUtility ? providerName : undefined}
        />
      </GaugeSlot>
      <List>
        {featureList.map((item) => (
          <ListRow key={item}>
            <ListRowIcon aria-hidden>
              <SvgIcon
                name={isUtility ? SvgNames.CloseFilled : SvgNames.CheckFilled}
                width={20}
                height={20}
                color={isUtility ? "#888CA7" : tokens.SUCCESS_60}
              />
            </ListRowIcon>
            <Typography size={12} color={tokens.BLACK}>
              {item}
            </Typography>
          </ListRow>
        ))}
      </List>
    </ListSection>
  );

  if (layout === "landscape") {
    return (
      <LandscapeCard data-testid={`offer-review-comparison-card-${variant}`}>
        {header}
        <ContentRow>
          {illustration}
          {listSection}
        </ContentRow>
      </LandscapeCard>
    );
  }

  return (
    <PortraitCard data-testid={`offer-review-comparison-card-${variant}`}>
      {header}
      {illustration}
      {listSection}
    </PortraitCard>
  );
};

const cardSurface = `
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
`;

const PortraitCard = styled.article`
  ${cardSurface}
  padding: 24px 20px 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 520px;
`;

const LandscapeCard = styled.article`
  ${cardSurface}
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 439px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  padding: 22px;

  @media (max-width: 900px) {
    flex-direction: column;
    min-height: unset;
    padding: 20px 16px 24px;
    gap: 16px;
  }
`;

const ContentRow = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;

  @media (max-width: 900px) {
    padding: 0;
  }
`;

const CardHeader = styled.div<{ $landscape: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  ${({ $landscape }) =>
    $landscape
      ? `
    padding-top: 4px;
  `
      : ""}
`;

const RecapBadge = styled.span<{ variant: "outline" | "filled" }>`
  padding: 4px 14px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  ${({ variant }) =>
    variant === "filled"
      ? `
    background: #000;
    color: #fff;
  `
      : `
    background: transparent;
    color: #000;
    border: 1px solid #000;
  `}
`;

const DesignLabel = styled.span`
  padding: 4px 24px;
  border-radius: 32px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  background: #000;
  color: #fff;
`;

const RecapHeadline = styled(Typography)`
  width: 100%;
  line-height: 1.25;
  text-align: center;
`;

const RecapBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const RecapBodyLine = styled(Typography)`
  display: block;
  line-height: 1.4;
  text-align: center;
`;

const ListSection = styled.div<{ $landscape: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  ${({ $landscape }) =>
    $landscape ? "margin-top: auto;" : "margin-top: auto;"}

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const GaugeSlot = styled.div`
  flex-shrink: 0;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  margin-top: auto;
  padding: 0;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 8px;
`;

const ListRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
`;

const ListRowIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;

  svg {
    display: block;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

export { OfferReviewComparisonInfographicCard };
export type { ComparisonLayout, OfferReviewComparisonInfographicCardProps };
